/* eslint-disable import/no-cycle */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';

import { Tooltip, Spinner } from '@oca/ui';
import { RecordFlowIcon } from '@oca/icons';
import { i18n } from '@lib/i18n';
import { notify } from '@lib/notifier';

import { fetchRecordflowToken } from '../api';

const RECORDFLOW_URL = 'https://recordflow.goavisa.com';

export const RecordFlowButton = React.memo(function RecordFlowButton({
  collapsed,
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await fetchRecordflowToken();
      const token = data && data.token;

      if (!token) {
        notify.error(i18n._(t`Failed to obtain RecordFlow token`));
        return;
      }

      window.location.href = `${RECORDFLOW_URL}?token=${encodeURIComponent(
        token,
      )}`;
    } catch (error) {
      const detail =
        error && error.data && (error.data.detail || error.data.message);
      notify.error(detail || i18n._(t`Failed to obtain RecordFlow token`));
    } finally {
      setLoading(false);
    }
  };

  const label = <Trans>Record Flow</Trans>;

  return (
    <Tooltip
      title={label}
      tooltipShown={collapsed ? undefined : false}
      placement="right"
    >
      <ActionButton
        type="button"
        onClick={handleClick}
        disabled={loading}
        aria-label={i18n._(t`Open Record Flow`)}
      >
        <IconBadge>
          {loading ? <Spinner size={18} /> : <RecordFlowIcon />}
        </IconBadge>

        <InfoContainer $hidden={collapsed}>
          <TitleRow>
            <TitleText>{label}</TitleText>
            <ArrowIcon>&rarr;</ArrowIcon>
          </TitleRow>
          <SubText>
            <Trans>Portal Integration</Trans>
          </SubText>
        </InfoContainer>
      </ActionButton>
    </Tooltip>
  );
});

RecordFlowButton.propTypes = {
  collapsed: PropTypes.bool.isRequired,
};

const ActionButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  padding: 0 7px;
  margin: 0;
  border: 1px solid #e9e8f6;
  background: #fbfbfe;
  border-radius: 10px;
  cursor: pointer;
  outline: none;
  text-align: left;
  user-select: none;
  overflow: hidden;
  white-space: nowrap;
  transition: all 180ms ease;

  &:hover:not(:disabled) {
    background: #f5f3ff;
    border-color: #d8b4fe;
    box-shadow: 0 2px 8px rgba(123, 68, 244, 0.08);
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: rgba(123, 68, 244, 0.1);
  margin-right: 10px;
  transition: all 180ms ease;

  svg {
    width: 16px;
    height: 16px;
    fill: #7b44f4;
    transition: fill 180ms ease;
  }

  ${ActionButton}:hover & {
    background: #7b44f4;

    svg {
      fill: #ffffff;
    }
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transform: translate3d(${({ $hidden }) => ($hidden ? -8 : 0)}px, 0, 0);
  transition: opacity 200ms ease, transform 200ms ease;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowIcon = styled.span`
  font-size: 12px;
  color: #7b44f4;
  margin-left: 4px;
  transition: transform 150ms ease;

  ${ActionButton}:hover & {
    transform: translateX(2px);
  }
`;

const SubText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  margin-top: 2px;
`;
